/**
 * Takes an input field and a list of tenants and filters the list based on the input field.
 *
 * @example
 * ```html
 * <div class="js-tenant-search">
 *   <input type="search" class="js-tenant-search-input">
 *   <ul class="js-tenant-list">
 *     <li class="js-tenant-search-item">
 *       <span
 *         data-searchable
 *         data-searchable-highlight
 *       >
 *         Tenant 1
 *       </span>
 *       <span
 *         data-searchable="exact"
 *         data-searchable-highlight
 *       >
 *         abc123
 *       </span>
 *     </li>
 *   </ul>
 *   <p class="js-no-results hidden">No results found</p>
 * </div>
 * ```
 *
 * ```css
 * ::highlight(search-results) {
 *   color: #000;
 *   background-color: #ff0;
 *   -webkit-text-stroke-color: #000;
 *   -webkit-text-stroke-width: 0.25px;
 * }
 * ```
 */
import { on } from "delegated-events";
import { observe } from "selector-observer";
import { isIgnoredKey } from "../utils/keyboard";
observe(".js-tenant-search", {
    initialize(container) {
        const items = [...container.querySelectorAll(".js-tenant-search-item")];
        const countFiltered = container.querySelector("[data-tenant-count-filtered]");
        const tenantList = container.querySelector(".js-tenant-list");
        const noResults = container.querySelector(".js-no-results");
        if (!tenantList) {
            console.error("Tenant list not found");
            return;
        }
        const allTextNodes = findSearchableNodes(tenantList);
        on("search", ".js-tenant-search-input", searched);
        on("keyup", ".js-tenant-search-input", searched);
        function searched(e) {
            // Prevents control keys from triggering the search such as arrow keys in all browsers, and escape in chromium which clears the input
            if (e instanceof KeyboardEvent && isIgnoredKey(e.key)) {
                return;
            }
            const target = e.target;
            const term = target.value.toLowerCase();
            const filtered = filterElements(term);
            highlightMatches(filtered, term, allTextNodes);
            setItemVisibility(filtered);
            setFilteredCount(filtered);
            handleNoResults(filtered);
        }
        function filterElements(term) {
            var _a, _b;
            if (term.length === 0) {
                return items;
            }
            const filteredItems = [];
            for (const row of items) {
                for (const node of allTextNodes) {
                    if (!row.contains(node)) {
                        continue;
                    }
                    const text = (_b = (_a = node.textContent) === null || _a === void 0 ? void 0 : _a.toLowerCase()) !== null && _b !== void 0 ? _b : "";
                    const exactMatch = matchStyle(node.parentElement) === "exact";
                    const isExactMatch = exactMatch && text.trim() === term;
                    const isContainsMatch = !exactMatch && text.includes(term);
                    if (isExactMatch || isContainsMatch) {
                        filteredItems.push(row);
                        break;
                    }
                }
            }
            return filteredItems;
        }
        function setItemVisibility(filtered) {
            for (const row of items) {
                if (filtered.includes(row)) {
                    row.removeAttribute("hidden");
                }
                else {
                    row.setAttribute("hidden", "");
                }
            }
        }
        function setFilteredCount(filtered) {
            if (countFiltered instanceof HTMLElement) {
                countFiltered.textContent = filtered.length.toLocaleString();
            }
        }
        function handleNoResults(filtered) {
            if (filtered.length === 0) {
                tenantList === null || tenantList === void 0 ? void 0 : tenantList.setAttribute("hidden", "");
                noResults === null || noResults === void 0 ? void 0 : noResults.removeAttribute("hidden");
            }
            else {
                tenantList === null || tenantList === void 0 ? void 0 : tenantList.removeAttribute("hidden");
                noResults === null || noResults === void 0 ? void 0 : noResults.setAttribute("hidden", "");
            }
        }
    },
});
function matchStyle(el) {
    if (!el) {
        return null;
    }
    const expectedMatchStyle = el.getAttribute("data-searchable");
    if (expectedMatchStyle === "exact") {
        return "exact";
    }
    return "contains";
}
function findSearchableNodes(el) {
    const treeWalker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, (node) => {
        var _a;
        if ((_a = node.parentElement) === null || _a === void 0 ? void 0 : _a.hasAttribute("data-searchable")) {
            return NodeFilter.FILTER_ACCEPT;
        }
        return NodeFilter.FILTER_SKIP;
    });
    const allTextNodes = [];
    while (treeWalker.nextNode()) {
        allTextNodes.push(treeWalker.currentNode);
    }
    return allTextNodes;
}
function highlightMatches(elements, term, allTextNodes) {
    var _a;
    // If the browser doesn't support CSS highlights, don't bother doing anything
    if (!CSS.highlights) {
        return;
    }
    CSS.highlights.clear();
    // If the search term is empty then there's nothing to highlight
    if (!term) {
        return;
    }
    const ranges = [];
    const termTrimmed = term.trim();
    for (const el of allTextNodes) {
        if (!el.parentElement) {
            continue;
        }
        const text = (_a = el.textContent) === null || _a === void 0 ? void 0 : _a.toLowerCase();
        const row = el.parentElement.closest(".js-tenant-search-item");
        const highlightMatch = el.parentElement.getAttribute("data-searchable-highlight") === "true";
        if (!highlightMatch || !text || !row || !elements.includes(row)) {
            continue;
        }
        const indices = [];
        const matchExact = matchStyle(el.parentElement) === "exact";
        if (matchExact) {
            if (text.trim() === termTrimmed) {
                const index = text.indexOf(term);
                indices.push(index);
            }
        }
        else {
            let startPos = 0;
            while (startPos < text.length) {
                const index = text.indexOf(term, startPos);
                if (index === -1) {
                    break;
                }
                indices.push(index);
                startPos = index + term.length;
            }
        }
        ranges.push(indices.map((index) => {
            const range = new Range();
            range.setStart(el, index);
            range.setEnd(el, index + term.length);
            return range;
        }));
    }
    if (ranges.length === 0) {
        return;
    }
    const searchResultsHighlight = new Highlight(...ranges.flat());
    CSS.highlights.set("search-results", searchResultsHighlight);
}
